import {
  PublicClientApplication,
  type AccountInfo,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { authentication } from "@microsoft/teams-js";
import { config } from "../config";

/**
 * Client-only authentication for the Teams Lite tab.
 *
 * Strategy (no backend required):
 *  1. Try MSAL silent token acquisition from the shared localStorage cache.
 *  2. If interaction is required, use the Teams authentication popup which
 *     drives an MSAL redirect flow inside a lightweight popup window
 *     (see public/auth-start.html and public/auth-end.html).
 *  3. Outside of Teams (plain browser dev), fall back to an MSAL popup.
 */

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: config.clientId,
    authority: config.authority,
    redirectUri: `${window.location.origin}/auth-end.html`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
});

let msalReady: Promise<void> | null = null;

function ensureMsal(): Promise<void> {
  if (!msalReady) {
    msalReady = msalInstance.initialize().then(async () => {
      // Capture a token response when returning from a full-page redirect
      // sign-in (fallback used when popups are blocked).
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
        }
      } catch {
        // Ignore: no redirect response pending.
      }
    });
  }
  return msalReady;
}

function getAccount(): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

/** Attempts to get a Graph access token without any user interaction. */
async function getTokenSilent(): Promise<string | null> {
  await ensureMsal();
  const account = getAccount();
  if (!account) {
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [...config.graphScopes],
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return null;
    }
    throw error;
  }
}

/** Runs the interactive sign-in via the Teams authentication popup. */
async function getTokenViaTeamsPopup(): Promise<string> {
  const query = new URLSearchParams({
    clientId: config.clientId,
    authority: config.authority,
    scopes: config.graphScopes.join(","),
  });
  const token = await authentication.authenticate({
    url: `${window.location.origin}/auth-start.html?${query.toString()}`,
    width: 600,
    height: 535,
  });
  // The popup returns the access token as a plain string through notifySuccess.
  return token;
}

/** Fallback for running outside of Teams (e.g. local browser preview). */
async function getTokenViaMsalPopup(): Promise<string> {
  await ensureMsal();
  try {
    const result = await msalInstance.acquireTokenPopup({
      scopes: [...config.graphScopes],
    });
    return result.accessToken;
  } catch (error) {
    // Popup blocked or unavailable (embedded browsers, strict blockers):
    // fall back to a full-page redirect. The response returns to
    // auth-end.html (the registered redirect URI), which forwards the auth
    // hash back to the app root where handleRedirectPromise completes it.
    await msalInstance.acquireTokenRedirect({
      scopes: [...config.graphScopes],
    });
    // acquireTokenRedirect navigates away; this promise never resolves.
    return new Promise<string>(() => {});
  }
}

/**
 * Returns a Microsoft Graph access token, prompting the user only when needed.
 * @param insideTeams Whether the app is currently hosted inside Microsoft Teams.
 */
export async function getGraphToken(insideTeams: boolean): Promise<string> {
  const silent = await getTokenSilent();
  if (silent) {
    return silent;
  }
  return insideTeams ? getTokenViaTeamsPopup() : getTokenViaMsalPopup();
}

/**
 * Silent-only Graph token attempt (no interaction, no popup). Returns null
 * when the user needs to sign in interactively.
 */
export async function getGraphTokenSilent(): Promise<string | null> {
  return getTokenSilent();
}

/**
 * True single sign-on inside Teams: returns the Teams client's SSO token for
 * this app (audience api://<domain>/<clientId>) without any user interaction.
 * The Meeting Angelo API exchanges it for a Graph token via the OBO flow.
 * Throws when SSO is unavailable (e.g. resource not configured, user must consent).
 */
export async function getTeamsSsoToken(): Promise<string> {
  return authentication.getAuthToken({ silent: true });
}

/** Interactive Graph sign-in for explicit user action (SignInCard button). */
export async function signInInteractive(insideTeams: boolean): Promise<string> {
  return insideTeams ? getTokenViaTeamsPopup() : getTokenViaMsalPopup();
}
